import { ToggleButton } from "~/react/ui/toggle-button"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Tooltip, TooltipTrigger, TooltipContent } from "~/react/ui/tooltip"
import { t } from "../i18n"

interface EditorToolbarProps {
  onInsertAttribute?: () => void
  onAttachFile?: () => void
  onToggleBold?: () => void
  onToggleItalic?: () => void
  onToggleStrike?: () => void
  onToggleBulletList?: () => void
  onToggleNumberedList?: () => void
  onInsertLink?: () => void
  onInsertImage?: () => void
  onInsertVideo?: () => void
  allowEmailAttachments: boolean
  allowEmailImages: boolean
  allowVideoLinks?: boolean
  isBoldActive?: boolean
  isItalicActive?: boolean
  isStrikeActive?: boolean
  disabled?: boolean
}

export function EditorToolbar({
  onInsertAttribute,
  onAttachFile,
  onToggleBold,
  onToggleItalic,
  onToggleStrike,
  onToggleBulletList,
  onToggleNumberedList,
  onInsertLink,
  onInsertImage,
  onInsertVideo,
  allowEmailAttachments,
  allowEmailImages,
  allowVideoLinks = false,
  isBoldActive = false,
  isItalicActive = false,
  isStrikeActive = false,
  disabled = false,
}: EditorToolbarProps) {
  return (
    <div className="trix-toolbar">
      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleButton
            variant="ghost"
            size="xs"
            iconOnly
            onClick={onToggleBold}
            selected={isBoldActive}
            disabled={disabled}
          >
            <Icon icon="bold" />
          </ToggleButton>
        </TooltipTrigger>
        <TooltipContent>{t("js.trix_format.bold")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleButton
            variant="ghost"
            size="xs"
            iconOnly
            onClick={onToggleItalic}
            selected={isItalicActive}
            disabled={disabled}
          >
            <Icon icon="italic" />
          </ToggleButton>
        </TooltipTrigger>
        <TooltipContent>{t("js.trix_format.italic")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <ToggleButton
            variant="ghost"
            size="xs"
            iconOnly
            onClick={onToggleStrike}
            selected={isStrikeActive}
            disabled={disabled}
          >
            <Icon icon="strikethrough" />
          </ToggleButton>
        </TooltipTrigger>
        <TooltipContent>{t("js.trix_format.strikethrough")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="xs" iconOnly onClick={onToggleBulletList} disabled={disabled}>
            <Icon icon="list-ul" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("js.trix_format.bullet_list")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="xs" iconOnly onClick={onToggleNumberedList} disabled={disabled}>
            <Icon icon="list-ol" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("js.trix_format.numbered_list")}</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="xs" iconOnly onClick={onInsertLink} disabled={disabled}>
            <Icon icon="link" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t("js.trix_format.insert_link")}</TooltipContent>
      </Tooltip>

      {allowEmailImages && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="xs" iconOnly onClick={onInsertImage} disabled={disabled}>
              <Icon icon="image" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("js.trix_format.insert_image")}</TooltipContent>
        </Tooltip>
      )}

      {allowVideoLinks && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="xs" iconOnly onClick={onInsertVideo} disabled={disabled}>
              <Icon icon="video" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("js.trix_format.insert_video")}</TooltipContent>
        </Tooltip>
      )}

      {allowEmailAttachments && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="xs" iconOnly onClick={onAttachFile} disabled={disabled}>
              <Icon icon="paperclip" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t("js.campaigns.content.attach_file")}</TooltipContent>
        </Tooltip>
      )}

      <Button variant="ghost" size="xs" onClick={onInsertAttribute} disabled={disabled}>
        <Icon icon="brackets-curly" />
        {t("js.campaigns.content.insert_attribute")}
      </Button>
    </div>
  )
}
